<template>
  <div class="cs-step">
    <!-- Section 1: Source Identification Card -->
    <div class="cs-source-card cs-source-card--meta">
      <div class="cs-source-card__head">
        <span class="cs-source-card__title">Source Identification</span>
        <span class="cs-source-card__hint">Choose a unique, descriptive name for this composite source</span>
      </div>

      <v-text-field
        :value="model.state.name"
        label="Source name"
        placeholder="e.g. Equipment Maintenance Overview"
        outlined
        dense
        counter="200"
        maxlength="200"
        :readonly="readonly"
        :error-messages="nameError ? [nameError] : []"
        hide-details="auto"
        class="cs-step__name"
        @input="onNameInput" />
    </div>

    <!-- Section 2: Starting Data Root Selector Studio -->
    <div class="cs-source-card cs-source-card--data">
      <div class="cs-source-card__head">
        <div class="cs-source-card__title-group">
          <span class="cs-source-card__title">Starting Data (Root Table)</span>
          <div class="cs-source-card__hint">
            The foundation of this composite source. All joined relations and calculated fields hang off the records in this table.
          </div>
        </div>

        <div v-if="selectedOption" class="cs-selected-pill">
          <span class="cs-selected-pill__dot"></span>
          <span class="cs-selected-pill__text">
            Root: <strong>{{ selectedOption.label }}</strong>
          </span>
        </div>
      </div>

      <div v-if="rootReadonly" class="cs-root-lock" role="note">
        <v-icon size="15" class="cs-root-lock__icon">mdi-lock-outline</v-icon>
        <span>{{ rootChangeHint }}</span>
      </div>

      <!-- Industrial Filter Buttons (No Icons) -->
      <div class="cs-ds-tabs" role="group" aria-label="Filter starting data by type">
        <button
          type="button"
          class="cs-ds-tab"
          :class="{ 'cs-ds-tab--active': activeFamilyTab === 'all' }"
          :aria-pressed="String(activeFamilyTab === 'all')"
          @click="activeFamilyTab = 'all'">
          <span class="cs-ds-tab__label">All Sources</span>
          <span class="cs-ds-tab__badge">{{ options.length }}</span>
        </button>

        <button
          v-for="family in availableFamilies"
          :key="family"
          type="button"
          class="cs-ds-tab"
          :class="{ 'cs-ds-tab--active': activeFamilyTab === family }"
          :aria-pressed="String(activeFamilyTab === family)"
          @click="activeFamilyTab = family">
          <span class="cs-ds-tab__label">{{ family }}</span>
          <span class="cs-ds-tab__badge">{{ countForFamily(family) }}</span>
        </button>
      </div>

      <!-- Search Toolbar -->
      <div class="cs-source-toolbar">
        <v-text-field
          v-model="search"
          aria-label="Filter starting data by name"
          placeholder="Filter starting data by name..."
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="cs-source-search" />

        <div class="cs-source-toolbar__stats">
          <span>{{ visibleOptions.length }} available</span>
          <span v-if="search" class="cs-columns__muted">(filtered from {{ options.length }})</span>
        </div>
      </div>

      <!-- State: Loading -->
      <div v-if="loading" class="cs-step__empty" role="status" aria-live="polite">
        <v-progress-circular indeterminate size="26" width="3" color="primary" />
        <span class="cs-step__empty-text">Loading candidate tables from facility...</span>
      </div>

      <!-- State: Empty Target Options -->
      <div v-else-if="!options.length" class="cs-step__empty" role="status">
        <v-icon size="32" color="primary">mdi-database-alert-outline</v-icon>
        <span v-if="rootOptionsFilteredOut" class="cs-step__empty-text">
          Everything available to this target belongs to a group rather than to the target itself.
          A source takes its scope from what it is built on, and group scope is not supported yet.
        </span>
        <span v-else class="cs-step__empty-text">
          Nothing is available for this target yet. Create the data you want to build on, then
          reopen this wizard.
        </span>
      </div>

      <!-- State: Empty Search -->
      <div v-else-if="!visibleOptions.length" class="cs-step__empty" role="status">
        <v-icon size="28" color="primary">mdi-magnify-close</v-icon>
        <span class="cs-step__empty-text">No starting data matches "{{ search }}".</span>
      </div>

      <!-- Selectable Options List (Fills height with internal scroll) -->
      <div v-else class="cs-source-list" role="radiogroup" aria-label="Starting data">
        <div
          v-for="option in visibleOptions"
          :key="option.key"
          :ref="`root-option-${option.key}`"
          role="radio"
          class="cs-source-row"
          :class="{
            'cs-source-row--selected': option.key === selectedRootKey,
            'cs-source-row--disabled': readonly || rootReadonly || !option.selectable
          }"
          :aria-checked="String(option.key === selectedRootKey)"
          :aria-disabled="String(readonly || rootReadonly || !option.selectable)"
          :aria-label="rootOptionAriaLabel(option)"
          :tabindex="rootOptionTabIndex(option)"
          @click="selectRoot(option)"
          @keydown.enter.prevent="selectRoot(option)"
          @keydown.space.prevent="selectRoot(option)"
          @keydown.down.prevent="moveRootSelection(option, 1)"
          @keydown.right.prevent="moveRootSelection(option, 1)"
          @keydown.up.prevent="moveRootSelection(option, -1)"
          @keydown.left.prevent="moveRootSelection(option, -1)">
          <!-- Radio Indicator -->
          <div class="cs-source-row__radio">
            <div class="cs-radio-box" :class="{ 'cs-radio-box--checked': option.key === selectedRootKey }">
              <span v-if="option.key === selectedRootKey" class="cs-radio-dot">&#10003;</span>
            </div>
          </div>

          <!-- Main Info -->
          <div class="cs-source-row__body">
            <div class="cs-source-row__title">
              <span class="cs-source-row__name">{{ option.label }}</span>
              <span v-if="option.key === selectedRootKey" class="cs-active-badge">Active Root</span>
            </div>
            <div v-if="!option.selectable" class="cs-source-row__reason">
              <v-icon size="13" color="warning" class="cs-reason-icon">mdi-alert-outline</v-icon>
              <span>{{ option.unavailableReason }}</span>
            </div>
          </div>

          <!-- Kind Chip -->
          <div class="cs-source-row__kind">
            <v-chip x-small outlined class="cs-kind-chip">
              {{ option.kindLabel }}
            </v-chip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { log } from '@/utils/debugLogger';
  import { humanizeNodeKind } from '../../model/definitionVocabulary';
  import { describeRoot, rootKeyOf } from '../../model/definitionModelHelpers';

  /**
   * Step 1 — name the source and choose the data it is built from.
   *
   * Enhanced with:
   * 1. 100% Height Fitting (zero outer scrollbar, internal scroll on options list only).
   * 2. Guaranteed single-row header alignment (Root indicator strictly right-aligned).
   * 3. Industrial category tabs (All Sources, Registers, Task Boards, Tags, AI findings). The
   *    Tags tab lists one row per tag kind, so it is the tag root's tag-kind picker (AVA-560).
   *    Rows are selected by `option.key` (kind and id), because a finding root has no id and
   *    a tag root's id is a tag kind rather than an entity id.
   * 4. Clear active selection indicator with checkmark radio badge.
   * 5. Prominent diagnostic badges for unmaterialized/unavailable tables.
   */
  export default {
    name: 'StepSource',

    props: {
      /** definitionModel for this authoring session. */
      model: { type: Object, required: true },
      /** Authoring graph for the saved definition, or null before the first save. */
      graph: { type: Object, default: null },
      /** Selected workspace target: {kind, id, name}. */
      target: { type: Object, required: true },
      /** Candidate roots the wizard loaded for this target. */
      rootOptions: { type: Array, default: () => [] },
      /** True while the wizard is still loading those candidates. */
      rootOptionsLoading: { type: Boolean, default: false },
      /** How many the target returned that belong to another scope and cannot be a root here. */
      rootOptionsFilteredOut: { type: Number, default: 0 },
      /** Server-side complaint about the name, e.g. a duplicate. */
      nameError: { type: String, default: null },
      /** True when the whole step renders as a viewer, mutating nothing. */
      readonly: { type: Boolean, default: false },
      /** True after first persistence: the name remains editable, but the root identity does not. */
      rootReadonly: { type: Boolean, default: false },
    },

    data() {
      return {
        search: '',
        activeFamilyTab: 'all', // 'all' | family name (e.g. 'Registers', 'Task boards')
      };
    },

    computed: {
      options() {
        return this.rootOptions;
      },

      loading() {
        return this.rootOptionsLoading;
      },

      selectedRootKey() {
        return rootKeyOf(this.model.state.root);
      },

      selectedOption() {
        if (!this.selectedRootKey) return null;
        const listed = this.options.find((o) => o.key === this.selectedRootKey);
        if (listed) return listed;

        const root = this.model.state.root;
        return {
          key: this.selectedRootKey,
          label: root.rootRegisterId && !root.label ? root.rootRegisterId : describeRoot(root),
          kindLabel: humanizeNodeKind(root.kind),
        };
      },

      rootChangeHint() {
        return 'To use a different root, create a new source.';
      },

      availableFamilies() {
        const families = [];
        this.options.forEach((opt) => {
          if (opt.family && families.indexOf(opt.family) === -1) {
            families.push(opt.family);
          }
        });
        return families;
      },

      visibleOptions() {
        let list = this.options;

        // 1. Filter by family tab
        if (this.activeFamilyTab !== 'all') {
          list = list.filter((opt) => opt.family === this.activeFamilyTab);
        }

        // 2. Filter by search query
        const term = (this.search || '').trim().toLowerCase();
        if (!term) return list;
        return list.filter((option) => option.label.toLowerCase().indexOf(term) !== -1);
      },
    },

    methods: {
      countForFamily(family) {
        return this.options.filter((opt) => opt.family === family).length;
      },

      rootOptionTabIndex(option) {
        if (this.readonly || this.rootReadonly || !option.selectable) return -1;

        const checkedOption = this.visibleOptions.find((candidate) => (
          candidate.key === this.selectedRootKey && candidate.selectable
        ));
        if (checkedOption) return option.key === checkedOption.key ? 0 : -1;

        const firstSelectable = this.visibleOptions.find((candidate) => candidate.selectable);
        return firstSelectable && firstSelectable.key === option.key ? 0 : -1;
      },

      rootOptionAriaLabel(option) {
        const kind = option.kindLabel ? `, ${option.kindLabel}` : '';
        const unavailable = option.unavailableReason ? `, unavailable: ${option.unavailableReason}` : '';
        return `${option.label}${kind}${unavailable}`;
      },

      moveRootSelection(option, direction) {
        if (this.readonly || this.rootReadonly) return;
        const choices = this.visibleOptions.filter((candidate) => candidate.selectable);
        if (!choices.length) return;

        const currentIndex = choices.findIndex((candidate) => candidate.key === option.key);
        const origin = currentIndex === -1 ? (direction > 0 ? -1 : 0) : currentIndex;
        const nextIndex = (origin + direction + choices.length) % choices.length;
        const nextOption = choices[nextIndex];
        this.selectRoot(nextOption);
        this.$nextTick(() => this.focusRootOption(nextOption.key));
      },

      focusRootOption(optionKey) {
        const refs = this.$refs[`root-option-${optionKey}`];
        const element = Array.isArray(refs) ? refs[0] : refs;
        if (element) element.focus();
      },

      onNameInput(value) {
        this.model.setName(value);
        this.$emit('name-input');
      },

      /**
       * Re-selecting the current row is a no-op the model already handles gracefully.
       */
      selectRoot(option) {
        if (this.readonly || this.rootReadonly
          || !option.selectable || option.key === this.selectedRootKey) return;

        log('[StepSource] root selected', { id: option.id, kind: option.kind });
        this.model.setRoot({ kind: option.kind, rootRegisterId: option.id, label: option.label });
        this.$emit('root-changed');
      },
    },
  };
</script>

<style scoped>
  .cs-step {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    gap: 12px;
  }

  .cs-columns__muted {
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  /* ---------------- Section Cards ---------------- */
  .cs-source-card {
    background: var(--ds-surface-elevated);
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-sm);
    box-shadow: var(--ds-shadow-1);
  }

  /* Fixed Height Meta Card */
  .cs-source-card--meta {
    flex: 0 0 auto;
    padding: 10px 14px 12px;
  }

  /* Expandable Starting Data Card */
  .cs-source-card--data {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 10px 14px 14px;
    overflow: hidden;
  }

  /* Single-Row Header Layout */
  .cs-source-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
    flex-wrap: nowrap;
    flex-shrink: 0;
  }

  .cs-source-card__title-group {
    flex: 1;
    min-width: 0;
  }

  .cs-source-card__title {
    font-family: var(--ds-font-family-heading);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v-text-base);
    white-space: nowrap;
  }

  .cs-source-card__hint {
    font-size: 0.75rem;
    color: var(--v-text-base);
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cs-step__name {
    margin-top: 4px;
    max-width: 100%;
  }

  .cs-step__name ::v-deep .v-input__control {
    min-height: 34px;
  }

  .cs-step__name ::v-deep input {
    font-size: 0.8125rem;
    font-weight: 500;
  }

  /* Active Root Selected Pill (Always Right-Aligned on Single Row) */
  .cs-selected-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 2px;
    background: var(--ds-surface-active);
    border: 1px solid var(--v-primary-base);
    font-size: 0.75rem;
    color: var(--v-text-base);
    flex-shrink: 0;
    white-space: nowrap;
    max-width: 380px;
  }

  .cs-selected-pill__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--v-secondary-base);
    flex-shrink: 0;
  }

  .cs-selected-pill__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ---------------- Industrial Tabs (No Icons) ---------------- */
  .cs-ds-tabs {
    display: flex;
    background: var(--v-bg7-base);
    border: 1px solid var(--ds-border-default);
    border-radius: 2px 2px 0 0;
    border-bottom: none;
    overflow-x: auto;
    flex-shrink: 0;
  }

  .cs-ds-tab {
    padding: 7px 14px;
    border: none;
    border-right: 1px solid var(--ds-border-default);
    border-bottom: 2px solid transparent;
    background: transparent;
    color: var(--v-text-base);
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    transition: all 0.15s ease;
  }

  .cs-ds-tab:hover {
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
  }

  .cs-ds-tab--active {
    background: var(--ds-surface-elevated);
    color: var(--v-text-base);
    font-weight: 600;
    border-bottom-color: var(--v-primary-base);
  }

  .cs-ds-tab__label {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cs-ds-tab__badge {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.6875rem;
    padding: 1px 5px;
    border-radius: 2px;
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
  }

  .cs-ds-tab--active .cs-ds-tab__badge {
    background: var(--ds-surface-active);
    color: var(--v-text-base);
    font-weight: 600;
  }

  /* ---------------- Search Toolbar ---------------- */
  .cs-source-toolbar {
    padding: 6px 12px;
    background: var(--v-bg2-base);
    border: 1px solid var(--ds-border-default);
    border-bottom: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
  }

  .cs-source-search {
    max-width: 320px;
  }

  .cs-source-search ::v-deep .v-input__control {
    min-height: 30px;
  }

  .cs-source-search ::v-deep input {
    font-size: 0.75rem;
  }

  .cs-source-toolbar__stats {
    font-size: 0.75rem;
    color: var(--v-text-base);
  }

  /* ---------------- Selectable Rows List (Flex: 1 & Internal Scroll Only) ---------------- */
  .cs-source-list {
    border: 1px solid var(--ds-border-default);
    border-radius: 0 0 2px 2px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1px;
    background: var(--v-bg7-base);
  }

  .cs-source-row {
    background: var(--ds-surface-elevated);
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
  }

  .cs-source-row:hover:not(.cs-source-row--disabled) {
    background: var(--ds-surface-hover);
  }

  .cs-root-lock {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    padding: 6px 10px;
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-sm);
    background: var(--ds-surface-active);
    color: var(--v-text-base);
    font-size: 0.75rem;
  }

  .cs-root-lock__icon {
    color: var(--v-primary-base);
  }

  .cs-source-row:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }

  .cs-source-row--selected {
    background: var(--ds-surface-active) !important;
    border-left: 3px solid var(--v-primary-base);
  }

  .cs-source-row--disabled {
    opacity: 0.55;
    cursor: not-allowed;
    background: var(--v-bg2-base);
  }

  /* Radio Indicator Box */
  .cs-source-row__radio {
    flex-shrink: 0;
  }

  .cs-radio-box {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1.5px solid var(--ds-border-strong);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--ds-surface-elevated);
    transition: all 0.15s ease;
  }

  .cs-radio-box--checked {
    border-color: var(--v-primary-base);
    background: var(--v-primary-base);
  }

  .cs-radio-dot {
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
  }

  /* Body */
  .cs-source-row__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .cs-source-row__title {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--v-text-base);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cs-source-row__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cs-active-badge {
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    padding: 1px 6px;
    border-radius: 2px;
    background: var(--v-primary-base);
    color: #ffffff;
    flex-shrink: 0;
  }

  .cs-source-row__reason {
    font-size: 0.6875rem;
    color: var(--v-text-base);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .cs-reason-icon {
    margin-top: -1px;
  }

  .cs-source-row__kind {
    flex-shrink: 0;
  }

  .cs-kind-chip {
    font-size: 0.625rem;
    height: 18px;
  }

  /* Empty States */
  .cs-step__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 36px 16px;
    text-align: center;
    border: 1px solid var(--ds-border-default);
    border-radius: 0 0 2px 2px;
    background: var(--v-bg2-base);
    flex: 1;
    min-height: 0;
  }

  .cs-step__empty-text {
    font-size: 0.8125rem;
    color: var(--v-text-base);
    max-width: 480px;
  }
</style>
